package com.crowpay.views.components.project.work.changeOrders.tables

import com.crowpay.ChangeRequest
import com.crowpay.ChangeType
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.Formats
import com.crowpay.extensions.handleOne
import com.crowpay.extensions.isUnread
import com.crowpay.utils.JumpTo
import com.crowpay.format
import com.crowpay.sdk.currentSession
import com.crowpay.sdk.lensUnread
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.utils.renderDollars
import com.crowpay.views.components.expandAndMarkAsReadAction
import com.crowpay.views.components.project.work.changeOrders.ChangeRequestItemWrapper
import com.crowpay.views.components.project.work.changeOrders.ChangeStatus
import com.crowpay.views.components.project.work.changeOrders.cancelled
import com.crowpay.views.components.project.work.changeOrders.status
import com.crowpay.views.components.expandIcon
import com.crowpay.views.components.project.work.changeOrders.ChangeItemReadReceipts
import com.crowpay.views.components.renderPriceChange
import com.crowpay.views.components.sectionIndentCol
import com.crowpay.views.components.space
import com.crowpay.views.components.unreadIconVisible
import com.crowpay.views.dialogs.ChangesRejectedDetails
import com.crowpay.views.screens.common.ProjectView
import com.crowpay.views.theming.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.px
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import kotlinx.datetime.Instant

fun ProjectView.renderReviewedChangeOrder(
    writer: ViewWriter,
    changeOrder: Readable<ChangeRequest>,
    changeItems: Readable<List<ChangeRequestItemWrapper>>,
    cardinal: Boolean
) = with(writer) {
    val expanded = Property(false)
    val commonStatus = shared {
        changeItems().map { it.status }.toSet().singleOrNull()
    }
    val orderUnread = currentSession.lensUnread {
        it.changeRequests.handleOne { changeOrder() }
    }

    val changeItemsUnread = sharedSuspending {
        ChangeItemReadReceipts.handleDelayUpdate(changeItems())
    }
    val feedbackUnread = sharedSuspending {
        ChangeItemReadReceipts.handleFeedback(changeItems())
    }

    val itemExpandedCache = HashMap<UUID, Property<Boolean>>()

    col {
        spacing = 0.px
        expandButtonTheme - button {
            dynamicTheme {
                if (expanded()) SectionSemantic else null
            }
            row {
                spacing = AppDimensions.expandButtonSpace
                centered - unreadIconVisible { orderUnread()() || changeItemsUnread()() || feedbackUnread()() }
                centered - expandIcon(expanded)
                subTitle {
                    ::content {
                        val c = changeOrder()
                        "Change #${c.number}: ${c.title}"
                    }
                }
            }
            action = expandAndMarkAsReadAction(expanded, shared { orderUnread() + changeItemsUnread() })
        }

        onlyWhen { expanded() } - sectionIndentCol {
            spacing = 0.px
            space(1.rem)
            body {
                ::content {
                    "Description: ${changeOrder().description}"
                }
            }
            space(1.rem)

            sizeConstraints(height = 2.rem) - row {
                spacing = 1.rem
                if (cardinal) weight(5f) - space() else weight(5f) - bodyBold("Work Items")
                weight(1f) - centered - bodyBold {
                    align = Align.Center
                    content = "ID"
                }
                weight(1.5f) - centered - bodyBold {
                    align = Align.Center
                    content = "Submitted"
                }
                weight(2.5f) - centered - bodyBold {
                    align = Align.Center
                    content = "Status"
                }
                weight(1f) - centered - bodyBold {
                    align = Align.Center
                    content = "Original"
                }
                weight(1f) - centered - bodyBold {
                    align = Align.Center
                    content = "Total"
                }
                weight(1.5f) - centered - bodyBold {
                    align = Align.Center
                    content = "Date"
                }
            }
            greySeparator()

            fun ViewWriter.tableRow(
                expanded: Readable<Boolean>? = null,
                allowExpand: Boolean = true,
                label: ReactiveContext.() -> Pair<String, ChangeType>?,
                id: ReactiveContext.() -> String,
                submitted: ReactiveContext.() -> Long?,
                status: ReactiveContext.() -> ChangeStatus?,
                original: ReactiveContext.() -> Long?,
                date: ReactiveContext.() -> Instant?,
                isUnread: ReactiveContext.() -> Boolean
            ): ViewModifiable {
                return row {
                    spacing = 1.rem
                    if (expanded == null)
                        weight(5f) - centered - bodyBold("Group Summary")
                    else
                        weight(5f) - row {
                            spacing = AppDimensions.expandButtonSpace
                            centered - unreadIconVisible { isUnread() }
                            if (allowExpand) centered - expandIcon(expanded)
                            centered - body {
                                ::content {
                                    label()?.first ?: ""
                                }
                            }
                            centered - smallBody {
                                ::content {
                                    label()?.second?.postTense ?: ""
                                }
                            }
                        }
                    weight(1f) - centered - body {
                        align = Align.Center
                        ::content.invoke(id)
                    }
                    weight(1.5f) - stack {
                        centered - renderPriceChange(true, submitted)
                    }
                    weight(2.5f) - centered - body {
                        dynamicTheme {
                            status()?.let { ForegroundSemantic(it.color) }
                        }
                        align = Align.Center
                        ::content { status()?.displayName ?: "-" }
                    }
                    weight(1f) - centered - body {
                        align = Align.Center
                        ::content { original()?.renderDollars() ?: "-" }
                    }
                    weight(1f) - centered - body {
                        align = Align.Center
                        ::content {
                            val o = original()
                            val s = submitted()
                            if (o != null && s != null) (o + s).renderDollars() else "-"
                        }
                    }
                    weight(1.5f) - centered - body {
                        align = Align.Center

                        ::content {
                            date()?.format(Formats.mmddyyyy) ?: "-"
                        }
                    }
                }
            }

            if (cardinal) col {
                spacing = 0.px
                sizeConstraints(height = 3.rem) - tableRow(
                    label = { null },
                    id = { changeOrder().number },
                    submitted = { changeItems().sumOf { it.priceChange ?: 0 } },
                    status = { commonStatus() },
                    original = { if (commonStatus() == null) null else 0 },
                    date = {
                        changeItems().map { it.changeAccepted ?: it.changeRejected }.toSet().singleOrNull()
                    },
                    isUnread = { false }
                )
            }
            greySeparator()
            col {
                spacing = 0.px
                forEach(changeItems) { changeItem ->
                    changeRequestItemButton(changeItem, cache = itemExpandedCache) { itemExpanded ->
                        JumpTo.ChangeItem.newTarget(changeItem._id, this@changeRequestItemButton) {
                            expanded.value = true
                            if (!changeItem.cancelled) itemExpanded.value = true
                        }
                        tableRow(
                            itemExpanded,
                            allowExpand = !changeItem.cancelled,
                            label = { Pair(changeItem.name, changeItem.changeType) },
                            id = { changeItem.itemNumber },
                            submitted = { if (changeItem.cancelled) -changeItem.originalPrice else changeItem.priceChange },
                            status = {
                                if (cardinal && commonStatus() != null) null
                                else changeItem.status
                            },
                            original = {
                                if (cardinal && commonStatus() != null) null
                                else changeItem.originalPrice
                            },
                            date = {
                                changeItem.let { it.changeAccepted ?: it.changeRejected }
                            },
                            isUnread = {
                                changeItem.readBy.isUnread(notNullSession().userId)
                            }
                        )
                    }
                }
            }
            space {
                existsDefaultFalse { changeItems().any { it.changeRejected != null } }
            }
            atEnd - secondaryButton - button {
                existsDefaultFalse { changeItems().any { it.changeRejected != null } }
                specCenteredTextWithUnread("View Concerns") { feedbackUnread()() }
                onClick {
                    dialogScreenNavigator.navigate(
                        ChangesRejectedDetails(
                            changeOrder(),
                            changeItems(),
                            client().name,
                            contractor().name
                        )
                    )
                }
            }
            space()
        }

        greySeparator { ::exists { !expanded() } }
    }
}